import * as React from 'react';

import { Checkbox, Radio, Typography } from '@material-ui/core';
import some from 'lodash/some';

import * as Api from '../../../weborder/Api';
import { ToCurrency } from '../../../weborder/BasketControl';
import { Async, Sorted } from '../../../weborder/Data';
import * as Store from '../../../weborder/Store';

interface Props {
  AddItem: (item: Api.MenuItem) => any;
  Currency: Api.Currency;
  Current: number;
  Item: Api.MenuItemOptionSet;
  Languages: Store.Language[];
  LiveUpdate: (options: Api.MenuItemOptionSetItem[], isDone: boolean) => any;
  ParentItem: Async<Api.MenuItem>;
  Show: boolean;
  translate: any;
}

interface State {
  ClickedDone?: boolean;
  Options: Api.MenuItemOptionSetItem[];
}

// const isMultiSelect = function(option: Api.MenuItemOptionSet): boolean {
//   return option.MinSelectCount === 0 && option.MaxSelectCount > 1;
// };

const isSingleSelect = function(option: Api.MenuItemOptionSet): boolean {
  return option.MinSelectCount === 1 && option.MaxSelectCount === 1;
};

export class OptionSet extends React.Component<Props, State> {
  constructor(props: Props, context: any) {
    super(props, context);

    this.state = {
      Options: [],
      ClickedDone: false,
    };
  }

  public UNSAFE_componentWillReceiveProps(newProps: Props) {
    //Prevent done-ness check if navigating between screens
    if (newProps.Current != this.props.Current) {
      this.setState({ Options: [], ClickedDone: false });
      return;
    }
  }

  private Title = (option: Api.MenuItemOptionSet) => {
    let prefix = '';

    if (option.Name) {
      prefix = option.Name + ' - ';
    }

    const { MinSelectCount, MaxSelectCount } = option;

    // Choose an exact number of items
    if (MinSelectCount === MaxSelectCount) {
      // One
      if (MaxSelectCount === 1) {
        return prefix + this.props.translate('Choose_one');
      }

      // Many
      return prefix + this.props.translate('Choose') + ' ' + MaxSelectCount;
    }

    if (MinSelectCount === 0 && MaxSelectCount > 0) {
      return (
        prefix + this.props.translate('Choose_up_to') + ' ' + MaxSelectCount
      );
    }

    if (MinSelectCount === 0 && MaxSelectCount === -1) {
      return prefix + this.props.translate('Choose_any');
    }

    if (MinSelectCount > 0 && MaxSelectCount != -1) {
      return (
        prefix +
        this.props.translate('Choose') +
        ' ' +
        MinSelectCount +
        ' - ' +
        MaxSelectCount
      );
    }

    if (MinSelectCount > 0 && MaxSelectCount === -1) {
      return (
        prefix +
        this.props.translate('Choose') +
        ' ' +
        MinSelectCount +
        ' ' +
        this.props.translate('Or_more')
      );
    }

    return this.props.translate('Select_an_option');
  };

  private optionsUpdated = (newOptions: Api.MenuItemOptionSetItem[]) => {
    // Check done-ness.
    // TODO Add more cases here.
    this.setState({ Options: newOptions }, () => {
      //Do a live update
      this.props.LiveUpdate(newOptions, this.isDone());
    });
  };

  private hasMinimumNeeded = (): boolean => {
    return this.state.Options.length >= this.props.Item.MinSelectCount;
  };

  private isDone = (): boolean => {
    return (
      this.state.ClickedDone ||
      this.state.Options.length === this.props.Item.MaxSelectCount
    );
  };

  private handleSelectOption(option: Api.MenuItemOptionSetItem) {
    const newOptions = this.state.Options.concat([option]);
    this.optionsUpdated(newOptions);
  }

  private handleRemoveOption(needle: Api.MenuItemOptionSetItem) {
    // Keep all items that are not needle.
    const newOptions = this.state.Options.filter(
      (it) => it.MenuItemOptionSetItemId != needle.MenuItemOptionSetItemId,
    );
    this.optionsUpdated(newOptions);
  }

  private handleOptionRadioToggled(
    allOptions: Api.MenuItemOptionSet,
    option: Api.MenuItemOptionSetItem,
  ) {
    const toRemove = allOptions.MenuItemOptionSetItems.map(
      (it) => it.MenuItemOptionSetItemId,
    );
    const newOptions = this.state.Options.filter(
      (it) => toRemove.indexOf(it.MenuItemOptionSetItemId) === -1,
    ).concat([option]);
    this.optionsUpdated(newOptions);
  }

  public render() {
    if (!this.props.Show) {
      return null;
    }

    let initialLang = 'en';

    for (const Language of this.props.Languages) {
      if (Language.active) {
        initialLang = Language.code;
        break;
      }
    }

    const item = this.props.Item;
    const selected = (m: Api.MenuItemOptionSetItem): boolean => {
      return some(
        this.state.Options,
        (o) => o.MenuItemOptionSetItemId === m.MenuItemOptionSetItemId,
      );
    };

    // //Modifier modifier check
    // const isModifierModifier =
    //   item.MenuItemOptionSetItems.findIndex(
    //     (menuItemOptionSetItem) => menuItemOptionSetItem.NextMenuItemOptionSetId != null
    //   ) != -1;

    // TODO: this price function is copied from ConfigureItem.tsx renderSelected
    const price = (o: Api.MenuItemOptionSetItem): JSX.Element | null => {
      if (!o.Price) {
        return null;
      }

      const plus = item.IsMasterOptionSet ? '' : '+';
      return (
        <Typography>
          {plus}
          {ToCurrency(o.Price, this.props.Currency, initialLang)}
        </Typography>
      );
    };

    const checkbox = (m: Api.MenuItemOptionSetItem) => {
      const callback = selected(m)
        ? () => this.handleRemoveOption(m)
        : () => this.handleSelectOption(m);

      let img: JSX.Element | null = null;
      if (m.ImageUrl !== null) {
        img = <img src={`${m.ImageUrl}?w=240&h=240`} alt="" />;
      }

      return (
        <div
          className="fd-menu-option-set__checkbox"
          key={m.MenuItemOptionSetItemId}
          onClick={callback}
        >
          {m.IsAvailable ? (
            <div key={`menu-item-${m.MenuItemOptionSetItemId}`}>
              <Checkbox onChange={callback} />
              {img}

              <Typography>{m.Name}</Typography>

              {price(m)}
            </div>
          ) : null}
        </div>
      );
    };

    let doneButton: JSX.Element | null = null;
    let bottomBar: JSX.Element | null = null;

    const done = () => {
      this.setState(
        {
          ClickedDone: true,
        },
        () => {
          this.props.LiveUpdate(this.state.Options, this.isDone());
        },
      );
    };

    if (this.hasMinimumNeeded()) {
      doneButton = (
        <button onClick={done} key="done-button">
          <Typography>{this.props.translate('Next')}</Typography>
        </button>
      );

      bottomBar = (
        <button onClick={done} disabled={false} key="done-button-bottom">
          <Typography>
            {`${this.props.translate('Next')} `}
            <span>&rarr;</span>
          </Typography>
        </button>
      );

      const { ParentItem, Current, AddItem } = this.props;

      if (ParentItem.status === 'success') {
        const optionSets = Sorted(ParentItem.data.MenuItemOptionSets);
        const allDone = Current === optionSets.length - 1;
        if (allDone) {
          doneButton = (
            <button
              disabled={!this.hasMinimumNeeded()}
              key="done-button"
              onClick={AddItem(ParentItem.data)}
            >
              <Typography>{this.props.translate('Add_to_basket')}</Typography>
            </button>
          );

          bottomBar = (
            <button
              disabled={!this.hasMinimumNeeded()}
              key="done-button-bottom"
              onClick={AddItem(ParentItem.data)}
            >
              <Typography>{this.props.translate('Add_to_basket')}</Typography>
            </button>
          );
        }
      }
    }

    if (isSingleSelect(item)) {
      doneButton = null;
      bottomBar = null;
    }

    const radioName: string = 'fd-option-' + item.MenuItemOptionSetId;
    const radio = (m: Api.MenuItemOptionSetItem) => {
      const on = selected(m);
      const onChange = () => {
        this.handleOptionRadioToggled(item, m);
      };
      let img: JSX.Element | null = null;
      if (m.ImageUrl != null) {
        img = <img src={`${m.ImageUrl}?w=240&h=240`} alt="" />;
      }

      return (
        <div
          className="fd-menu-option-set__radiobutton"
          key={m.MenuItemOptionSetItemId}
          onClick={() => this.handleOptionRadioToggled(item, m)}
        >
          {m.IsAvailable ? (
            <div key={`menu-item-${m.MenuItemOptionSetItemId}`}>
              <Radio
                value={on.toString()}
                onChange={onChange}
                name={radioName}
              />
              {img}

              <Typography>{m.Name}</Typography>

              {price(m)}
            </div>
          ) : null}
        </div>
      );
    };

    let controls: JSX.Element[] = [];

    if (isSingleSelect(item)) {
      controls = item.MenuItemOptionSetItems.map(radio);
    } else {
      controls = item.MenuItemOptionSetItems.map(checkbox);
    }

    return (
      <div>
        <Typography>{this.Title(item)}</Typography>

        <div>{controls}</div>

        <div className="text-center">{doneButton}</div>
        {bottomBar}
      </div>
    );
  }
}
